import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pause, Play, Trash2, Edit2, Clock, AlertCircle, Check, X, RefreshCw } from 'lucide-react';
import { recurringActionService } from '../../services/recurringActionService';
import type { QstashScheduleInfo, QstashSchedulesResponse } from '../../services/recurringActionService';
import type { RecurringAction, RecurringActionCreate, RecurringActionUpdate } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Cron human-readable display
// ---------------------------------------------------------------------------

function isSimpleField(field: string): boolean {
  return /^\d+$/.test(field) || field === '*';
}

function cronToHuman(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [minute, hour, , , dow] = parts;

  // Fall back to raw cron for complex expressions (ranges, lists, steps)
  if (!isSimpleField(minute) || !isSimpleField(hour)) return cron;

  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (isNaN(h) || isNaN(m)) return cron;

  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const time = `${h12}:${m.toString().padStart(2, '0')} ${period}`;

  if (dow === '*') return `Daily at ${time}`;
  if (dow === '1-5') return `Weekdays at ${time}`;
  if (dow === '0,6') return `Weekends at ${time}`;

  const dayNames: Record<string, string> = {
    '0': 'Sundays', '1': 'Mondays', '2': 'Tuesdays', '3': 'Wednesdays',
    '4': 'Thursdays', '5': 'Fridays', '6': 'Saturdays',
  };
  if (dayNames[dow]) return `${dayNames[dow]} at ${time}`;

  return `${cron} (${time})`;
}

// ---------------------------------------------------------------------------
// Schedule picker presets
// ---------------------------------------------------------------------------

type SchedulePreset = 'daily' | 'weekdays' | 'weekly' | 'custom';

const DAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

function buildCron(preset: SchedulePreset, hour: number, minute: number, weekday: string): string {
  switch (preset) {
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`;
    case 'weekly':
      return `${minute} ${hour} * * ${weekday}`;
    case 'custom':
      return `${minute} ${hour} * * *`; // fallback; user edits raw
  }
}

function parseCron(cron: string): { preset: SchedulePreset; hour: number; minute: number; weekday: string; raw: string } {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { preset: 'custom', hour: 9, minute: 0, weekday: '1', raw: cron };

  const [min, hr, , , dow] = parts;

  // Complex expressions (ranges, lists, steps) must use the custom raw editor
  // to avoid lossy round-trips through buildCron
  if (!isSimpleField(min) || !isSimpleField(hr)) {
    return { preset: 'custom', hour: 9, minute: 0, weekday: '1', raw: cron };
  }

  const hour = parseInt(hr, 10);
  const minute = parseInt(min, 10);
  if (isNaN(hour) || isNaN(minute)) return { preset: 'custom', hour: 9, minute: 0, weekday: '1', raw: cron };

  if (dow === '*') return { preset: 'daily', hour, minute, weekday: '1', raw: cron };
  if (dow === '1-5') return { preset: 'weekdays', hour, minute, weekday: '1', raw: cron };
  if (/^[0-6]$/.test(dow)) return { preset: 'weekly', hour, minute, weekday: dow, raw: cron };

  return { preset: 'custom', hour, minute, weekday: '1', raw: cron };
}

// ---------------------------------------------------------------------------
// Inline editor form
// ---------------------------------------------------------------------------

interface ActionFormProps {
  initial?: RecurringAction;
  userTimezone: string;
  onSave: (data: RecurringActionCreate | RecurringActionUpdate) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  isSystem?: boolean;
}

const ActionForm: React.FC<ActionFormProps> = ({ initial, userTimezone, onSave, onCancel, saving, isSystem }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [timezone, setTimezone] = useState(initial?.timezone ?? userTimezone);
  const [error, setError] = useState<string | null>(null);

  const initialCron = initial ? parseCron(initial.cron_expression) : { preset: 'daily' as SchedulePreset, hour: 9, minute: 0, weekday: '1', raw: '0 9 * * *' };
  const [preset, setPreset] = useState<SchedulePreset>(initialCron.preset);
  const [hour, setHour] = useState(initialCron.hour);
  const [minute, setMinute] = useState(initialCron.minute);
  const [weekday, setWeekday] = useState(initialCron.weekday);
  const [customCron, setCustomCron] = useState(initialCron.raw);

  const handleSubmit = async () => {
    if (!isSystem) {
      if (!name.trim()) { setError('Name is required'); return; }
      if (!prompt.trim()) { setError('Prompt is required'); return; }
    }

    const cron = preset === 'custom' ? customCron : buildCron(preset, hour, minute, weekday);
    // Basic 5-field check
    if (cron.trim().split(/\s+/).length !== 5) { setError('Cron expression must be exactly 5 fields'); return; }

    setError(null);

    if (isSystem) {
      // Only send schedule fields for system actions
      await onSave({
        cron_expression: cron,
        timezone,
      });
    } else {
      await onSave({
        name: name.trim(),
        prompt: prompt.trim(),
        cron_expression: cron,
        timezone,
      });
    }
  };

  return (
    <div className="space-y-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {isSystem && initial ? (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {initial.name}
        </div>
      ) : (
        <>
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Daily morning briefing"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agent Prompt</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={3}
              placeholder="Text me a summary of my tasks for today along with any calendar events..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>
        </>
      )}

      {/* Schedule */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {(['daily', 'weekdays', 'weekly', 'custom'] as SchedulePreset[]).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                preset === p
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {preset !== 'custom' ? (
          <div className="flex items-center gap-2">
            {preset === 'weekly' && (
              <select
                value={weekday}
                onChange={e => setWeekday(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400">at</span>
            <input
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={e => setHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
              className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">:</span>
            <input
              type="number"
              min={0}
              max={59}
              value={minute}
              onChange={e => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
              className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : (
          <input
            type="text"
            value={customCron}
            onChange={e => setCustomCron(e.target.value)}
            placeholder="0 6 * * *"
            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>

      {/* Timezone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timezone</label>
        <input
          type="text"
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
          placeholder="America/New_York"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">IANA timezone (e.g. America/New_York, Europe/London)</p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : initial ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Action card (shared between system and custom sections)
// ---------------------------------------------------------------------------

interface ActionCardProps {
  action: RecurringAction;
  isSystem: boolean;
  onEdit: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, isSystem, onEdit, onTogglePause, onDelete }) => (
  <div className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
    {/* Status indicator */}
    <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
      action.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'
    }`} />

    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {action.name}
        </span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
          action.status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
        }`}>
          {action.status === 'active' ? 'Active' : 'Paused'}
        </span>
      </div>
      {!isSystem && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
          {action.prompt}
        </p>
      )}
      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {cronToHuman(action.cron_expression)}
        </span>
        {action.timezone && <span>{action.timezone}</span>}
        {action.trigger_count > 0 && (
          <span className="flex items-center gap-1">
            <Check className="w-3 h-3" />
            {action.trigger_count} run{action.trigger_count !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>

    {/* Action buttons */}
    <div className="flex items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={onEdit}
        title="Edit schedule"
        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <Edit2 className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onTogglePause}
        title={action.status === 'active' ? 'Pause' : 'Resume'}
        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        {action.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      {!isSystem && (
        <button
          type="button"
          onClick={onDelete}
          title="Delete"
          className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// QStash quota panel
// ---------------------------------------------------------------------------

const QSTASH_MAX_SCHEDULES = 10;

const ScheduleQuotaPanel: React.FC<{ onCleanup: () => void }> = ({ onCleanup }) => {
  const [data, setData] = useState<QstashSchedulesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await recurringActionService.listQstashSchedules();
      setData(result);
    } catch {
      setError('Failed to load schedule quota');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleteOrphan = async (sched: QstashScheduleInfo) => {
    if (!sched.schedule_id) return;
    if (!window.confirm('Delete this orphaned schedule? This frees up a quota slot.')) return;
    setDeleting(sched.schedule_id);
    try {
      await recurringActionService.deleteQstashSchedule(sched.schedule_id);
      await load();
      onCleanup();
    } catch {
      setError('Failed to delete schedule');
    } finally {
      setDeleting(null);
    }
  };

  if (!data && loading) {
    return (
      <div className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">
        Loading quota...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-xs text-red-500 dark:text-red-400 py-2">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const used = data.total;
  const pct = Math.round((used / QSTASH_MAX_SCHEDULES) * 100);
  const isNearLimit = used >= QSTASH_MAX_SCHEDULES - 1;
  const orphans = data.schedules.filter(s => s.orphan);

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
          Schedule Quota
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isNearLimit ? 'bg-red-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <span className={`text-xs font-mono ${isNearLimit ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {used}/{QSTASH_MAX_SCHEDULES}
        </span>
      </div>

      {error && (
        <div className="text-xs text-red-500 dark:text-red-400 mb-2">{error}</div>
      )}

      {/* Orphan warning */}
      {orphans.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:underline"
          >
            <AlertCircle className="w-3 h-3" />
            {orphans.length} orphaned schedule{orphans.length !== 1 ? 's' : ''} using quota
            <span className="text-gray-400">{expanded ? '(hide)' : '(show)'}</span>
          </button>

          {expanded && (
            <div className="mt-2 space-y-1.5">
              {orphans.map((sched, i) => (
                <div
                  key={sched.schedule_id ?? i}
                  className="flex items-center justify-between gap-2 p-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-gray-700 dark:text-gray-300 truncate">
                      {sched.destination || 'unknown'}
                    </div>
                    {sched.cron && (
                      <div className="text-gray-500 dark:text-gray-400">{sched.cron}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteOrphan(sched)}
                    disabled={deleting === sched.schedule_id}
                    className="shrink-0 px-2 py-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                    title="Delete orphaned schedule"
                  >
                    {deleting === sched.schedule_id ? '...' : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const RecurringActionsSettings: React.FC = () => {
  const { user } = useAuth();
  const [actions, setActions] = useState<RecurringAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [quotaKey, setQuotaKey] = useState(0);

  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const loadActions = useCallback(async () => {
    try {
      const data = await recurringActionService.list(true);
      setActions(data);
    } catch {
      setError('Failed to load scheduled actions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadActions(); }, [loadActions]);

  const { systemActions, customActions } = useMemo(() => ({
    systemActions: actions.filter(a => a.system_action != null),
    customActions: actions.filter(a => a.system_action == null),
  }), [actions]);

  const refreshQuota = useCallback(() => setQuotaKey(k => k + 1), []);

  const handleCreate = async (data: RecurringActionCreate | RecurringActionUpdate) => {
    setSaving(true);
    try {
      await recurringActionService.create(data as RecurringActionCreate);
      setShowForm(false);
      await loadActions();
      refreshQuota();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.includes('502') || msg.includes('quota')
        ? 'Schedule quota exceeded. Delete unused actions or orphaned schedules to free up slots.'
        : 'Failed to create action');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string, data: RecurringActionCreate | RecurringActionUpdate) => {
    setSaving(true);
    try {
      await recurringActionService.update(id, data as RecurringActionUpdate);
      setEditingId(null);
      await loadActions();
    } catch {
      setError('Failed to update action');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePause = async (action: RecurringAction) => {
    try {
      if (action.status === 'active') {
        await recurringActionService.pause(action.id);
      } else {
        await recurringActionService.resume(action.id);
      }
      await loadActions();
    } catch {
      setError('Failed to update action status');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this scheduled action? This cannot be undone.')) return;
    try {
      await recurringActionService.delete(id);
      await loadActions();
      refreshQuota();
    } catch {
      setError('Failed to delete action');
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
        Loading scheduled actions...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* System Automations */}
      {systemActions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">System Automations</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Provisioned automatically. You can adjust their schedule or pause them.
          </p>
          <div className="space-y-2">
            {systemActions.map(action => (
              <div key={action.id}>
                {editingId === action.id ? (
                  <ActionForm
                    initial={action}
                    userTimezone={userTimezone}
                    onSave={(data) => handleUpdate(action.id, data)}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                    isSystem
                  />
                ) : (
                  <ActionCard
                    action={action}
                    isSystem
                    onEdit={() => { setEditingId(action.id); setShowForm(false); }}
                    onTogglePause={() => handleTogglePause(action)}
                    onDelete={() => handleDelete(action.id)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Actions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Automations</h3>
          {!showForm && (
            <button
              type="button"
              onClick={() => { setShowForm(true); setEditingId(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Automation
            </button>
          )}
        </div>

        {/* New action form */}
        {showForm && (
          <ActionForm
            userTimezone={userTimezone}
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
            saving={saving}
          />
        )}

        {/* Custom action list */}
        {customActions.length === 0 && !showForm ? (
          <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="w-6 h-6 mx-auto mb-1.5 opacity-50" />
            No custom actions yet.
          </div>
        ) : (
          <div className="space-y-2">
            {customActions.map(action => (
              <div key={action.id}>
                {editingId === action.id ? (
                  <ActionForm
                    initial={action}
                    userTimezone={userTimezone}
                    onSave={(data) => handleUpdate(action.id, data)}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                ) : (
                  <ActionCard
                    action={action}
                    isSystem={false}
                    onEdit={() => { setEditingId(action.id); setShowForm(false); }}
                    onTogglePause={() => handleTogglePause(action)}
                    onDelete={() => handleDelete(action.id)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ScheduleQuotaPanel key={quotaKey} onCleanup={loadActions} />
    </div>
  );
};
